import React from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Flex, Skeleton, Typography } from "antd";
import { SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import {
  FacebookFilled,
  GlobalOutlined,
  InstagramOutlined,
  MailOutlined,
  PhoneOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { ImagesCarousel, LocationMap } from "components/";

import {
  BusinessAddToFavoritesButton,
  BusinessDayWorkingHours,
  BusinessServicesList,
  BusinessSocialMediaLink,
  BusinessSocialMediaLinkSkeleton,
  BusinessStaffMember,
  BusinessStaffMemberSkeleton,
} from "../../components";
import { useBusiness } from "../../hooks";
import {
  ImagesCarouselWrapper,
  MainContentWrapper,
  PageWrapper,
  SideContentWrapper,
  BusinessStaffMembersSwiper,
  SwiperThemeOverride,
  MapContainer,
} from "./Business.styles";

const BusinessPage: React.FC = () => {
  const { t } = useTranslation();
  const { id: businessIdString } = useParams();
  const businessId = parseInt(businessIdString || "0", 10);

  const { loading, business } = useBusiness({ id: businessId });

  return (
    <PageWrapper>
      <SwiperThemeOverride />

      <MainContentWrapper>
        <ImagesCarouselWrapper>
          <ImagesCarousel
            loading={loading}
            images={business?.portfolio?.map((image) => ({ id: image.id!, url: image.url! })) || []}
          />
        </ImagesCarouselWrapper>

        <Flex justify="space-between">
          {loading && <Skeleton active paragraph={{ rows: 1 }} />}
          {!loading && (
            <Flex vertical>
              <Typography.Title level={2}>{business?.name}</Typography.Title>
              <Typography.Text type="secondary">{business?.address}</Typography.Text>
            </Flex>
          )}
          <Flex>{!loading && <BusinessAddToFavoritesButton isFavorite onClick={() => {}} />}</Flex>
        </Flex>

        <BusinessServicesList services={business?.staff?.flatMap((s) => s.services) || []} loading={loading} header />
      </MainContentWrapper>

      <SideContentWrapper>
        <MapContainer>
          {loading ? <Skeleton.Node active /> : <LocationMap latitude={52.4085115} longitude={16.9336645} />}
        </MapContainer>

        <Flex vertical>
          <Typography.Title level={5}>{t("business.details.aboutUs")}</Typography.Title>

          {!loading && <Typography.Text type="secondary">{business?.shortDescription}</Typography.Text>}
          {loading && <Skeleton active paragraph={false} />}
        </Flex>

        <Flex vertical>
          <Typography.Title level={5}>{t("business.details.staff")}</Typography.Title>

          <Flex gap={24}>
            <BusinessStaffMembersSwiper
              slidesPerView="auto"
              spaceBetween={8}
              pagination={{
                clickable: true,
              }}
              modules={[Autoplay]}
            >
              {loading
                ? Array.from({ length: 4 }).map((_, index) => (
                    <SwiperSlide key={`staff-member-skeleton-${index}`}>
                      <BusinessStaffMemberSkeleton />
                    </SwiperSlide>
                  ))
                : (business?.staff || []).map((staffMember) => (
                    <SwiperSlide key={staffMember.id}>
                      <BusinessStaffMember member={staffMember} />
                    </SwiperSlide>
                  ))}
            </BusinessStaffMembersSwiper>
          </Flex>
        </Flex>

        <Flex vertical>
          <Typography.Title level={5}>{t("business.details.businessHours")}</Typography.Title>

          {loading ? (
            <Skeleton active paragraph={{ rows: 7 }} title={false} />
          ) : (
            <Flex vertical gap={8}>
              {Array.from({ length: 7 }).map((_, index) => (
                <BusinessDayWorkingHours key={index} dayOfWeek={index} startTime="09:00" endTime="18:00" />
              ))}
            </Flex>
          )}
        </Flex>

        <Flex vertical>
          <Typography.Title level={5}>{t("business.details.contacts")}</Typography.Title>

          {!loading && (
            <Flex vertical gap={8}>
              {business?.phone && (
                <Flex gap={16}>
                  <PhoneOutlined />
                  <Typography.Text type="secondary">{business.phone}</Typography.Text>
                </Flex>
              )}
              {business?.email && (
                <Flex gap={16}>
                  <MailOutlined />
                  <Typography.Text type="secondary">{business.email}</Typography.Text>
                </Flex>
              )}
            </Flex>
          )}

          {loading && <Skeleton active paragraph={{ rows: 2 }} title={false} />}
        </Flex>

        <Flex vertical>
          <Typography.Title level={5}>{t("business.details.social")}</Typography.Title>

          <Flex gap={12} wrap>
            {loading && Array.from({ length: 6 }).map((_, index) => <BusinessSocialMediaLinkSkeleton key={index} />)}
            {!loading && (
              <>
                <BusinessSocialMediaLink icon={InstagramOutlined} link="https://instagram.com" />
                <BusinessSocialMediaLink icon={FacebookFilled} link="https://facebook.com" />
                <BusinessSocialMediaLink icon={WhatsAppOutlined} link="https://whatsapp.com" />
                <BusinessSocialMediaLink icon={GlobalOutlined} link="https://example.com" />
              </>
            )}
          </Flex>
        </Flex>
      </SideContentWrapper>
    </PageWrapper>
  );
};

export default BusinessPage;
